import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  X, Plus, Trash2, Settings, Zap, RefreshCw, CheckCircle, XCircle,
  ArrowLeft, ChevronDown, ClipboardList, Bot, Lock, CalendarCheck, Clock
} from 'lucide-react';
import {
  DEFAULT_MESSAGE_TEMPLATE, MSG_PLACEHOLDERS,
  AI_EMPLOYEES_TEMPLATE, AI_EMP_PLACEHOLDERS,
  DAILY_REPORT_TEMPLATE, DAILY_REPORT_PLACEHOLDERS,
} from './messagingTemplates';

const USE_CASE_META = {
  onboarding: { label: 'Onboarding', cls: 'bg-teal-400/10 text-teal-400 border-teal-400/30' },
  ai_employees: { label: 'AI Employees', cls: 'bg-violet-400/10 text-violet-400 border-violet-400/30' },
  daily_report: { label: 'End-of-Day Report', cls: 'bg-amber-400/10 text-amber-400 border-amber-400/30' },
};

const TEMPLATE_DEFAULTS = {
  onboarding: DEFAULT_MESSAGE_TEMPLATE,
  ai_employees: AI_EMPLOYEES_TEMPLATE,
  daily_report: DAILY_REPORT_TEMPLATE,
};

const PLACEHOLDER_SETS = {
  onboarding: MSG_PLACEHOLDERS,
  ai_employees: AI_EMP_PLACEHOLDERS,
  daily_report: DAILY_REPORT_PLACEHOLDERS,
};

const BROWSER_TZ = (() => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
})();

const MessagingManagerModal = ({ integration, workspaceId, api, isAdmin, items, onChanged, onClose }) => {
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [useCase, setUseCase] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showFields, setShowFields] = useState(false);
  const [cardTesting, setCardTesting] = useState(null);

  const isZapier = !!integration.zapier;

  const startCreate = () => {
    setEditing(null);
    setUseCase(null);
    setForm({ name: '', webhook_url: '', message_template: DEFAULT_MESSAGE_TEMPLATE, send_time: '18:00' });
    setTestResult(null);
    setView('form');
  };

  const pickUseCase = (uc) => {
    setUseCase(uc);
    setForm(p => ({ ...p, message_template: TEMPLATE_DEFAULTS[uc] || '', send_time: p.send_time || '18:00' }));
  };

  const startEdit = (item) => {
    setEditing(item);
    const uc = item.use_case || 'onboarding';
    setUseCase(uc);
    setForm({
      name: item.name || '',
      webhook_url: item.webhook_url || '',
      message_template: item.message_template || TEMPLATE_DEFAULTS[uc] || '',
      send_time: item.send_time || '18:00',
    });
    setTestResult(null);
    setView('form');
  };

  const save = async () => {
    if (!(form.webhook_url || '').trim().startsWith('http')) {
      toast.error('Paste a valid webhook URL');
      return;
    }
    setSaving(true);
    try {
      const templateVal = !isZapier ? (form.message_template || '') : '';
      if (editing) {
        await api.put(`/api/workspaces/${workspaceId}/integrations/messaging/${editing.integration_id}`, {
          name: form.name || '',
          webhook_url: form.webhook_url || '',
          message_template: templateVal,
          ...(useCase === 'daily_report' ? { send_time: form.send_time || '18:00', timezone: BROWSER_TZ } : {}),
        });
        toast.success('Integration updated');
      } else {
        await api.post(`/api/workspaces/${workspaceId}/integrations/messaging`, {
          provider: integration.id,
          name: form.name || '',
          use_case: useCase,
          webhook_url: form.webhook_url || '',
          message_template: templateVal,
          enabled: true,
          ...(useCase === 'daily_report' ? { send_time: form.send_time || '18:00', timezone: BROWSER_TZ } : {}),
        });
        toast.success(`${integration.name} integration created`);
      }
      await onChanged();
      setView('list');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const sendTest = async (url, template, uc, id) => {
    if (!(url || '').trim()) { toast.error('No webhook URL'); return; }
    if (id) setCardTesting(id); else setTesting(true);
    setTestResult(null);
    try {
      const res = await api.post(`/api/workspaces/${workspaceId}/integrations/messaging/test`, {
        provider: integration.id,
        webhook_url: url,
        message_template: isZapier ? '' : (template || ''),
        use_case: uc || 'onboarding',
      });
      if (!id) setTestResult(res.data);
      if (res.data.ok) toast.success(`Test sent (${res.data.status_code}) — check ${isZapier ? 'your Zap history' : 'your channel'}!`);
      else toast.error(`Webhook responded with ${res.data.status_code || 'an error'}`);
    } catch (e) {
      if (!id) setTestResult({ ok: false, error: e?.response?.data?.detail || 'Request failed' });
      toast.error(e?.response?.data?.detail || 'Test failed');
    }
    if (id) setCardTesting(null); else setTesting(false);
  };

  const toggleEnabled = async (item) => {
    try {
      await api.put(`/api/workspaces/${workspaceId}/integrations/messaging/${item.integration_id}`, {
        enabled: !item.enabled,
      });
      await onChanged();
    } catch (e) { toast.error('Failed to update'); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/api/workspaces/${workspaceId}/integrations/messaging/${item.integration_id}`);
      toast.success('Integration deleted');
      await onChanged();
    } catch (e) { toast.error('Failed to delete'); }
  };

  const Icon = integration.icon;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose} data-testid="messaging-manager-overlay">
      <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="w-full max-w-2xl bg-space-800 rounded-xl overflow-hidden max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} data-testid="messaging-manager-modal">

        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {view === 'form' && (
              <button onClick={() => setView('list')} className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10" data-testid="msg-manager-back">
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${integration.color}15` }}>
              <Icon size={18} style={{ color: integration.color }} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-white">
                {view === 'list' ? `${integration.name} Integrations` : editing ? `Edit — ${editing.name}` : `New ${integration.name} Integration`}
              </h3>
              {view === 'list' && <p className="text-xs text-gray-400">Create as many as you need — each with its own channel & use case</p>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/10" data-testid="msg-manager-close">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto">
          {view === 'list' ? (
            <>
              {items.length === 0 ? (
                <div className="text-center py-8">
                  <Icon size={28} className="mx-auto text-gray-600" style={{ color: `${integration.color}80` }} />
                  <p className="text-sm text-gray-400 mt-3">No {integration.name} integrations yet</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {items.map(item => {
                    const uc = USE_CASE_META[item.use_case || 'onboarding'] || USE_CASE_META.onboarding;
                    return (
                      <div key={item.integration_id}
                        className={`rounded-xl border p-4 ${item.enabled ? 'border-white/10 bg-white/[0.03]' : 'border-white/[0.06] bg-white/[0.01] opacity-70'}`}
                        data-testid="msg-integration-box">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-white truncate" data-testid="msg-integration-name">{item.name || `${integration.name} integration`}</p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              <span className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${uc.cls}`}>{uc.label}</span>
                              {(item.use_case === 'daily_report') && item.send_time && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-full border bg-white/5 text-gray-300 border-white/10" data-testid="msg-integration-send-time">
                                  <Clock size={9} /> {item.send_time}
                                </span>
                              )}
                            </div>
                          </div>
                          <button onClick={() => toggleEnabled(item)} title={item.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
                            className={`relative w-9 h-5 rounded-full shrink-0 transition-colors ${item.enabled ? 'bg-teal-500' : 'bg-white/10'}`}
                            data-testid="msg-integration-toggle">
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${item.enabled ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                          </button>
                        </div>
                        <p className="text-[11px] text-gray-500 mt-2 truncate font-mono">{item.webhook_url}</p>
                        <div className="flex items-center gap-1.5 mt-3">
                          <button onClick={() => sendTest(item.webhook_url, item.message_template, item.use_case, item.integration_id)}
                            disabled={cardTesting === item.integration_id}
                            className="p-1.5 rounded-lg bg-sky-400/10 text-sky-400 hover:bg-sky-400/20 disabled:opacity-50" title="Send test"
                            data-testid="msg-integration-test">
                            {cardTesting === item.integration_id ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={13} />}
                          </button>
                          <button onClick={() => startEdit(item)} className="p-1.5 rounded-lg bg-white/5 text-gray-400 hover:text-teal-400 hover:bg-teal-400/10" title="Edit"
                            data-testid="msg-integration-edit">
                            <Settings size={13} />
                          </button>
                          <button onClick={() => remove(item)} className="p-1.5 rounded-lg bg-white/5 text-gray-400 hover:text-red-400 hover:bg-red-400/10" title="Delete"
                            data-testid="msg-integration-delete">
                            <Trash2 size={13} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              <button onClick={startCreate}
                className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-white/15 text-sm text-gray-300 hover:border-teal-400/40 hover:text-teal-400 transition-colors"
                data-testid="msg-create-new-btn">
                <Plus size={15} /> Create New
              </button>
            </>
          ) : !useCase && !editing ? (
            /* Step 1 — use case picker */
            <div>
              <p className="text-sm text-gray-300 mb-4">What should this integration deliver?</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button onClick={() => pickUseCase('onboarding')}
                  className="text-left rounded-xl border border-white/10 bg-white/[0.03] p-4 hover:border-teal-400/40 transition-colors group"
                  data-testid="use-case-onboarding">
                  <div className="w-9 h-9 rounded-lg bg-teal-400/10 flex items-center justify-center">
                    <ClipboardList size={17} className="text-teal-400" />
                  </div>
                  <p className="text-sm font-semibold text-white mt-3 group-hover:text-teal-400">Client Onboarding</p>
                  <p className="text-xs text-gray-400 mt-1">Instant alerts when a client completes your onboarding form.</p>
                </button>
              </div>
            </div>
          ) : (
            /* Step 2 — config form */
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border ${(USE_CASE_META[useCase] || USE_CASE_META.onboarding).cls}`}>
                  {(USE_CASE_META[useCase] || USE_CASE_META.onboarding).label}
                </span>
                {!editing && (
                  <button onClick={() => setUseCase(null)} className="text-[11px] text-gray-500 hover:text-gray-300">change</button>
                )}
              </div>
              {integration.hint && useCase === 'onboarding' && (
                <div className="p-3 rounded-lg bg-blue-400/10 border border-blue-400/20">
                  <p className="text-xs text-blue-400">{integration.hint}</p>
                </div>
              )}
              {useCase === 'ai_employees' && (
                <div className="p-3 rounded-lg bg-violet-400/10 border border-violet-400/20">
                  <p className="text-xs text-violet-300">
                    Every daily auto-generated prompt and every prompt you save from the Strategist chat will be delivered to this webhook{isZapier ? ' as structured JSON' : ' as a formatted message'}.
                  </p>
                </div>
              )}
              {useCase === 'daily_report' && (
                <div className="p-3 rounded-lg bg-amber-400/10 border border-amber-400/20">
                  <p className="text-xs text-amber-300">
                    <strong>ONE message per day</strong> at your chosen time — a recap of every task your AI team completed since the previous report. Your employees work 24/7, so the report covers the full window (including late-night work from the day before), grouped by day.
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Name</label>
                <input value={form.name || ''} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                  placeholder='e.g., "Marketing Team Channel"'
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50"
                  data-testid="msg-form-name" />
              </div>
              {useCase === 'daily_report' && (
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Send report at</label>
                  <div className="flex items-center gap-3">
                    <input type="time" value={form.send_time || '18:00'}
                      onChange={(e) => setForm(p => ({ ...p, send_time: e.target.value }))}
                      className="px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:outline-none focus:border-amber-400/50 [color-scheme:dark]"
                      data-testid="msg-form-send-time" />
                    <span className="text-xs text-gray-500 flex items-center gap-1.5">
                      <Clock size={12} /> Your timezone: <span className="text-gray-300">{BROWSER_TZ}</span>
                    </span>
                  </div>
                </div>
              )}
              <div>
                <label className="block text-sm text-gray-400 mb-2">{isZapier ? 'Catch Hook URL' : 'Incoming Webhook URL'}</label>
                <input value={form.webhook_url || ''} onChange={(e) => setForm(p => ({ ...p, webhook_url: e.target.value }))}
                  placeholder={integration.fields?.[0]?.placeholder || 'https://...'}
                  className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-teal-400/50 font-mono"
                  data-testid="msg-form-webhook" />
              </div>
              {!isZapier && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm text-gray-400">Message template</label>
                    <button type="button" onClick={() => setForm(p => ({ ...p, message_template: TEMPLATE_DEFAULTS[useCase] || '' }))}
                      className="text-xs text-teal-400 hover:underline">Reset to default</button>
                  </div>
                  <textarea rows={8} value={form.message_template || ''}
                    onChange={(e) => setForm(p => ({ ...p, message_template: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg bg-white/5 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-teal-400/50 text-xs font-mono leading-relaxed"
                    data-testid="msg-form-template" />
                  <button type="button" onClick={() => setShowFields(v => !v)}
                    className="mt-1 flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-teal-400 transition-colors">
                    <ChevronDown size={14} className={`transition-transform ${showFields ? 'rotate-180' : ''}`} />
                    {showFields ? 'Hide' : 'View'} all {(PLACEHOLDER_SETS[useCase] || []).length} data fields
                  </button>
                  {showFields && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {(PLACEHOLDER_SETS[useCase] || []).map(key => (
                        <button key={key} type="button"
                          onClick={() => { navigator.clipboard.writeText(`{{${key}}}`); toast.success(`{{${key}}} copied`); }}
                          className="px-2 py-1 rounded-md bg-white/5 border border-white/10 text-[11px] font-mono text-teal-300 hover:bg-teal-400/10 transition-colors">
                          {`{{${key}}}`}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {isZapier && (
                <div className="p-3 rounded-lg bg-orange-400/10 border border-orange-400/20">
                  <p className="text-xs text-orange-300">
                    {useCase === 'daily_report'
                      ? <>Zapier receives the <strong>full daily report as structured JSON</strong> (date, task count, every task with employee + timestamp) — map it to any action in your Zap.</>
                      : useCase === 'ai_employees'
                        ? <>Zapier receives <strong>each prompt as structured JSON</strong> — map the fields to any action in your Zap.</>
                        : <>Zapier receives <strong>all onboarding fields as structured JSON</strong> — map them to any action in your Zap.</>}
                  </p>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2.5">
                <button type="button" onClick={() => sendTest(form.webhook_url, form.message_template, useCase)}
                  disabled={testing || !(form.webhook_url || '').trim()}
                  className="px-4 py-2 rounded-lg bg-sky-400/15 text-sky-400 border border-sky-400/30 text-xs font-semibold hover:bg-sky-400/25 transition-colors disabled:opacity-50 flex items-center gap-1.5"
                  data-testid="msg-form-test">
                  {testing ? <RefreshCw size={13} className="animate-spin" /> : <Zap size={13} />}
                  {testing ? 'Sending…' : (isZapier ? 'Send Test Data' : 'Send Test Message')}
                </button>
                {testResult && (testResult.ok ? (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-teal-400 bg-teal-400/10 px-3 py-1.5 rounded-lg">
                    <CheckCircle size={13} /> Delivered ({testResult.status_code})
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-xs font-medium text-red-400 bg-red-400/10 px-3 py-1.5 rounded-lg">
                    <XCircle size={13} /> {testResult.error || `Failed (${testResult.status_code})`}
                  </span>
                ))}
              </div>
              <div className="flex items-center gap-3 pt-2">
                <button onClick={() => setView('list')}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 text-white text-sm hover:bg-white/10 transition-colors">
                  Cancel
                </button>
                <button onClick={save} disabled={saving}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-teal-400 text-space-900 text-sm font-medium hover:bg-teal-500 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="msg-form-save">
                  {saving ? <RefreshCw size={15} className="animate-spin" /> : <Plus size={15} />}
                  {editing ? 'Save Changes' : 'Create Integration'}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default MessagingManagerModal;
